import { useEffect, useState } from 'react';
import { ApiError } from '../../lib/api';
import type { TextKnobMeta } from './constants';

/** Free-text knob (morning brief focus notes / routine URL). Saves on blur
    through the same PATCH as the number knobs. Any failure keeps the typed
    draft and shows the reason under the field; the next blur retries. The
    card disables it while any knob save is in flight, because the server
    merges knobs by read-modify-write and parallel saves could clobber. */
export function TextKnobInput({
  id,
  meta,
  value,
  isMobile,
  disabled = false,
  onSave,
}: {
  id: string;
  meta: TextKnobMeta;
  value: string;
  isMobile: boolean;
  disabled?: boolean;
  onSave: (v: string) => Promise<void>;
}) {
  const [draft, setDraft] = useState(value);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => setDraft(value), [value]);

  const commit = () => {
    if (draft.trim() === value.trim()) {
      setError(null);
      return;
    }
    if (draft.trim().length > meta.max) {
      setError(`At most ${meta.max} characters.`);
      return;
    }
    setError(null);
    onSave(draft).catch((e) => {
      setError(e instanceof ApiError && e.status === 400
        ? e.message
        : `Couldn't save${e instanceof Error && e.message ? ` (${e.message})` : ''}. Your text is kept; click away again to retry.`);
    });
  };

  const common = {
    id,
    className: `f-input atm-text-input${error ? ' invalid' : ''}`,
    value: draft,
    disabled,
    placeholder: meta.placeholder,
    'aria-invalid': error ? true : undefined,
    'aria-describedby': error ? `${id}-error` : undefined,
    onChange: (e: { target: { value: string } }) => setDraft(e.target.value),
    onBlur: commit,
  };

  return (
    <div className={`atm-knob text${isMobile ? ' m' : ''}`}>
      <label className="kl" htmlFor={id}>{meta.label}</label>
      {meta.multiline
        ? <textarea {...common} rows={3} />
        : <input {...common} type="url" onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }} />}
      {error && <span className="atm-knob-error" id={`${id}-error`} role="alert">{error}</span>}
    </div>
  );
}
